<template>
  <div>
    <UiTips
      v-if="documentationTipsAvailable"
      v-permission="TIPS_PERMISSION"
      :modal="isDocumentationTips"
      :tips="TIPS?.[title]?.Tips || ''"
      @updateToggleDocumentationTips="isDocumentationTips = $event"
    />
    <ValidationObserver @submit="applyFilters">
      <div ref="titleWrapper" class="w-full">
        <div class="flex relative flex-col justify-between items-start gap-2">
          <!-- Heading -->
          <div class="flex w-full justify-between items-end">
            <div>
              <div v-if="pageHeading" class="flex flex-wrap items-center gap-0.5 sm:gap-2">
                <span class="font-inter relative font-medium text-base xl:text-xl text-black">
                  {{ $t(`title.${pageHeading}`) }} {{ additionalName }}
                </span>
                <template v-if="tooltipTitle">
                  <template v-if="documentationTipsAvailable">
                    <span class="cursor-pointer" @click="toggleDocumentationTips">
                      <icon
                        class="icon"
                        :icon="isDocumentationTips ? 'bulbOn' : 'bulbOff'"
                        width="28"
                        height="28"
                        :color="isDocumentationTips ? 'warning-600' : 'primary-gray-700'"
                      />
                    </span>
                  </template>
                  <UiOnClickOutside v-else :do="closeToolTip">
                    <span
                      class="cursor-pointer"
                      @click="clickToolTip"
                      @mouseover="displayToolTip"
                      @mouseleave="hideToolTip"
                    >
                      <icon
                        class="icon"
                        icon="infoCircleUpdated"
                        color="primary-purple-600"
                        height="20.617"
                        width="16.5"
                      />
                      <div
                        v-if="showToolTip"
                        class="absolute -ml-4 mt-3 md:mt-2 rtl:-mr-24 md:rtl:-mr-7 z-40 max-w-[320px]"
                      >
                        <Tooltip
                          :title="$t(`title.${tooltipTitle}`)"
                          :text="$t(`toolTipInfo.${tooltipTitle}`)"
                          direction="left"
                        />
                      </div>
                    </span>
                  </UiOnClickOutside>
                </template>
              </div>
              <BreadCrumbs
                v-if="displayBreadcrumb"
                class="breadcrumpbs hidden md:flex"
                :display-breadcrumb="true"
              />
            </div>
            <!-- Section dropdown -->
            <div
              v-if="!isSectionHidden && routerLevel?.isSectionLevel"
              class="sm:hidden flex gap-2.5"
            >
              <div v-if="showSectionDropdown && isMultipleCampusesAllowed">
                <SectionDropdown :key="forceRerender" :hide-title="true" />
              </div>
            </div>
          </div>
          <div
            v-if="!rightbarHidden"
            class="flex flex-wrap-reverse items-center gap-2.5 justify-end w-full"
          >
            <div v-if="isMultipleButtonsAvailable && !isButtonsHidden" class="xl:hidden">
              <div class="flex flex-wrap gap-2.5 justify-end">
                <template
                  v-for="(button, index) in rightBarContent?.header?.buttons"
                  :key="index + button.title"
                >
                  <SplitButton
                    v-permission="button.permissions"
                    :buttons="button"
                    buttons-width="w-full"
                  />
                </template>
              </div>
            </div>
            <div v-if="isCalendarShow">
              <CalendarActionsWrapper
                :calendar-date="filterView === 'Week' ? dateTimeRange : currentDate"
                :filter-view="filterViews"
                :filters="filters"
                @selectDate="setCalendarDate"
                @selectDateRange="setCalendarDateRange"
                @setFilter="setFilter"
                @applyFilter="applyDateFilter"
              />
            </div>
            <div v-if="isAdditionalFilter">
              <UiSingleSelect
                v-model="additionalFilterValue"
                label="title"
                :hide-title="true"
                :no-error="true"
                :options="optionsList"
                :hide-un-select-icon="true"
                @change="updateAdditionalFilter"
              />
            </div>
            <div class="flex items-center gap-2.5">
              <!-- Filters -->
              <div class="flex items-center gap-3">
                <div
                  v-if="filterOption && isEnabledForProd"
                  class="xl:h-11 xl:w-[104px] md:w-10 md:h-10 w-9 h-9 flex items-center cursor-pointer justify-center gap-2 bg-white border rounded-lg"
                  :class="filterBorderColor"
                  @click="toggleFilter"
                >
                  <icon
                    icon="filter"
                    :height="filterIconSize"
                    :width="filterIconSize"
                    :color="filterIconColor"
                  />
                  <span
                    class="font-inter font-semibold text-primary-gray-700 text-sm hidden xl:block"
                    :class="filterTextColor"
                  >
                    Filters
                  </span>
                </div>
                <div
                  v-if="count"
                  class="badge z-1 relative justify-center flex items-center bg-primary-purple-600 border mb-5 border-primary-purple-600 text-white rounded-full text-sm w-6 h-6 rtl:-mr-7 ltr:-ml-7"
                >
                  <p class="items-center text-white p-1.5">{{ count }}</p>
                </div>
              </div>
              <div v-if="isSingleButtonsAvailable && !isButtonsHidden" class="xl:hidden">
                <div class="flex flex-wrap gap-2.5 justify-end">
                  <div
                    v-for="(button, index) in rightBarContent?.header?.buttons"
                    :key="index + button.title"
                  >
                    <SplitButton
                      v-permission="button.permissions"
                      :buttons="button"
                      buttons-width="w-full"
                    />
                  </div>
                </div>
              </div>
              <div v-if="isStatsAvailable && !isRightBarButtonHidden" class="xl:hidden flex">
                <MobileRightBar />
              </div>
            </div>
          </div>
        </div>
        <div v-if="filterOption" class="filter-block w-full">
          <transition name="slide-fade">
            <div
              v-if="isActive"
              class="mt-2 bg-white border border-primary-purple-200 rounded-lg relative px-4.5 sm:px-5 py-4 sm:py-[17.5px]"
            >
              <span class="absolute z-10 cursor-pointer top-3.5 right-[18px]" @click="toggleFilter">
                <icon class="icon" icon="cross" height="14" width="14" color="primary-purple-700" />
              </span>
              <div>
                <slot name="filterItems"></slot>
              </div>
              <div
                v-if="displayedColumnOptions?.length"
                :key="forceRerender + displayedColumnOptions.length"
                class="pb-5 border-b border-primary-purple-100"
              >
                <UiMultiSelect
                  v-model="selectedColumns"
                  title="SELECT_COLUMNS"
                  label="dataTableName"
                  input-color="bg-primary-white"
                  :options="displayedColumnOptions"
                  :already-selected="alreadySelectedDisplayedColumn"
                  class="text-text-color font-inter text-sm font-normal flex-1"
                />
              </div>
              <div
                class="footer flex justify-end pt-5"
                :class="[!displayedColumnOptions?.length && 'border-t border-primary-purple-100']"
              >
                <div class="flex w-full justify-between">
                  <div>
                    <UIButton class="sm-button" type="button" @click="emitClearEvent">
                      Clear
                    </UIButton>
                  </div>
                  <div class="flex gap-5">
                    <UIButton class="sm-button" type="button" @click="emitCancelEvent">
                      Cancel
                    </UIButton>
                    <UIButton class="sm-button" button="primary">Apply</UIButton>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import UiOnClickOutside from '@components/UiElements/UiOnClickOutside.vue'
import MobileRightBar from '@src/components/MobileRightBar.vue'
import BreadCrumbs from '@components/BreadCrumbs.vue'
import icon from '@components/icons/icon.vue'
import { mapState, mapGetters } from 'vuex'
import SectionDropdown from '@/src/router/layouts/left-bar/SectionDropdown.vue'
import { Form as ValidationObserver } from 'vee-validate'
import Tooltip from '@components/tooltip.vue'
import tooltipMixin from '@src/mixins/components/tooltip-mixin.js'
import { FEATURES_ENVIRONMENT, ALLOWED_FILTERS } from '@src/services/.env.js'
import UiMultiSelect from '@/src/components/UiElements/UiMultiSelect.vue'
import defaultLayout from '@/src/router/layouts/defaultLayout.json'
import SplitButton from '@components/SplitButton.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import CalendarActionsWrapper from '@components/Calendar/CalendarActionsWrapper.vue'
import { getCurrentDate } from '@/src/utils/moment.util.js'
import { formatDateString } from '@utils/format-date'
import UiSingleSelect from '@/src/components/UiElements/UiSingleSelect.vue'
import UiTips from '@/src/components/UiElements/UiTips.vue'
import TIPS from '@/src/constants/tips.js'
import isEmpty from 'lodash/isEmpty'
import { ENV } from '@src/constants/general-constants.js'
export default {
  name: 'TitleWrapper',
  components: {
    BreadCrumbs,
    ValidationObserver,
    icon,
    MobileRightBar,
    SectionDropdown,
    Tooltip,
    UiMultiSelect,
    UiOnClickOutside,
    SplitButton,
    UIButton,
    CalendarActionsWrapper,
    UiSingleSelect,
    UiTips,
  },
  mixins: [tooltipMixin],
  props: {
    isRightBarButtonHidden: { type: Boolean, default: false },
    isButtonsHidden: { type: Boolean, default: false },
    isSectionHidden: { type: Boolean, default: false },
    additionalName: {
      type: String,
      default: '',
    },
    count: {
      type: Number,
      default: 0,
    },
    statsTitle: {
      type: String,
      default: 'Title',
    },
    title: {
      type: String,
      default: '',
    },
    tooltipTitle: {
      type: String,
      default: '',
    },

    description: {
      type: String,
      default: '',
    },
    displayBreadcrumb: {
      type: Boolean,
      default: false,
    },
    filterOption: {
      type: Boolean,
      default: false,
    },
    displayedColumnOptions: {
      type: Array,
      default: () => [],
    },
    alreadySelectedDisplayedColumn: {
      type: Array,
      default: () => [],
    },
    filters: { type: Array, default: () => [] },
    filterViews: { type: String, default: '' },
    isCalendarShow: { type: Boolean, default: false },
    filterFooterClasses: {
      type: String,
      default: 'border-t border-primary-grey p-5',
    },
    isAdditionalFilter: { type: Boolean, default: false },
    optionsList: {
      type: Array,
      default: () => [],
    },
    defaultView: {
      type: String,
      default: '',
    },
  },
  emits: [
    'clear',
    'cancel',
    'applyFilter',
    'showFields',
    'applyDate',
    'updateAdditionalFilterValue',
  ],
  data() {
    return {
      ENV,
      FEATURES_ENVIRONMENT,
      TIPS,
      isNotTablet: false,
      pageHeading: '',
      isActive: false,
      dashboard: 'dashboard',
      selectedColumns: [],
      forceRerender: 0,
      isRightbarHidden: window.matchMedia('(max-width: 1279px)').matches,
      isMobileScreen: window.matchMedia('(max-width: 767px)').matches,
      dateTimeRange: null,
      currentDate: getCurrentDate(),
      additionalFilterValue: this.defaultView,
      isDocumentationTips: false,
      TIPS_PERMISSION: { env: [ENV.DEV] },
    }
  },

  computed: {
    ...mapGetters('layout', ['isMultipleCampusesAllowed']),
    ...mapState({
      tabRightNav: (state) => state?.layout?.tabRightNav,
      tabLeftNav: (state) => state?.layout?.tabLeftNav,
      currentSectionScope: (state) => state?.layout?.currentSectionScope,
      showSectionDropdown: (state) => state.layout.showSectionDropdown,
      showClassDropdown: (state) => state.layout.showClassDropdown,
      currentCampusScope: (state) => state?.layout?.currentCampusScope,
      currentClassScope: (state) => state?.layout?.currentClassScope,
      rightBarContent: (state) => state.layout.rightBarContent,
      routerLevel: (state) => state?.layout?.routerLevel,
      leftBarContent: (state) => state?.layout?.leftBarContent,
    }),
    documentationTipsAvailable() {
      return !!TIPS[this.title]
    },
    isMultipleButtonsAvailable() {
      return (
        this.isButtonsAvailables &&
        !this.rightbarHidden &&
        this.rightBarContent?.header?.buttons?.length > 1
      )
    },
    isSingleButtonsAvailable() {
      return (
        this.isButtonsAvailables &&
        !this.rightbarHidden &&
        this.rightBarContent?.header?.buttons?.length === 1
      )
    },
    filterBorderColor() {
      return this.isActive ? 'border-primary-purple-700' : 'border-primary-purple-200'
    },
    filterIconColor() {
      return this.isActive ? 'primary-purple-700' : 'primary-gray-700'
    },
    filterTextColor() {
      return this.isActive ? 'text-primary-purple-700' : 'text-primary-gray-700'
    },
    rightBarToggleIcon() {
      return this.tabRightNav ? 'doubleChevronLeft' : 'doubleChevronRight'
    },
    isButtonsAvailables() {
      return !this.isEmpty(this.rightBarContent?.header?.buttons)
    },
    iconColor() {
      return this.tabRightNav ? 'primary-purple-700' : 'primary-gray-700'
    },
    isStatsAvailable() {
      return this.rightBarContent?.stats?.length
    },
    rightbarHidden() {
      let isRightBarActive
      isRightBarActive = defaultLayout.rightBarHiddenFor.includes(this.$route?.name)
      if (!isRightBarActive)
        return (isRightBarActive = defaultLayout.RightBarActive.includes(this.$route?.path))
      return isRightBarActive
    },
    isEnabledForProd() {
      let isEnabled = false

      if (!['dev', 'prod'].includes(FEATURES_ENVIRONMENT)) return !isEnabled

      isEnabled = ALLOWED_FILTERS.includes(this.$route?.name)

      return isEnabled
    },
    isIntituteLevelChange() {
      return this.currentCampusScope + this.currentSectionScope
    },
    filterIconSize() {
      return this.isRightbarHidden ? '20' : '24'
    },
    isSecondaryButtonsDisplay() {
      return this.rightBarContent?.header?.buttons?.length > 1 && !this.rightbarHidden
    },
  },
  watch: {
    isIntituteLevelChange: {
      handler() {
        this.forceRerender++
      },
    },
    title: {
      handler() {
        if (this.$route && this.$route.meta.pageHeading) {
          this.pageHeading = this.$route.meta.pageHeading
        } else {
          this.pageHeading = this.title || ''
        }
      },
      immediate: true,
    },
    currentSectionScope: {
      handler() {
        this.forceRerender++
      },
    },
  },
  /**
   * Created Hook
   * @description Sets the title of page
   */
  mounted() {
    this.setTitleWrapperHeight()
    this.resizeObserver = new ResizeObserver(() => {
      this.setTitleWrapperHeight()
    })

    if (this.$refs.titleWrapper) {
      this.resizeObserver.observe(this.$refs.titleWrapper)
    }
  },

  beforeUnmount() {
    if (this.resizeObserver && this.$refs.titleWrapper) {
      this.resizeObserver.unobserve(this.$refs.titleWrapper)
    }
  },

  methods: {
    isEmpty,
    getCurrentDate,
    toggleDocumentationTips() {
      this.isDocumentationTips = !this.isDocumentationTips
    },
    updateAdditionalFilter() {
      this.$emit('updateAdditionalFilterValue', this.additionalFilterValue)
    },

    emitClearEvent() {
      this.toggleFilter()
      this.$emit('clear')
    },
    emitCancelEvent() {
      this.toggleFilter()
      this.$emit('cancel')
    },

    applyFilters() {
      this.$emit('applyFilter', { displayedColumns: this.selectedColumns })
      this.$emit('showFields')
      this.toggleFilter()
    },
    /**
     * toggle filter
     * @description toggling the value of isActive data variable
     */
    toggleFilter() {
      this.isActive = !this.isActive
    },
    setCalendarDate(dt) {
      this.currentDate = dt
    },
    setCalendarDateRange(dtr) {
      this.tempDate.dateTimeRange = this.dateTimeRange
      this.dateTimeRange = dtr

      // function use For set Range for Weekly call
      this.initializeFilterForWeek(this.dateTimeRange.startDate, this.dateTimeRange.endDate)

      this.forceRenderCalendar++
    },
    initializeFilterForWeek(startingDate, endingDate = null) {
      const startDate = new Date(startingDate)
      const endDate = new Date(startingDate)

      // Setting this to only get range for 7 days apart
      endDate.setDate(endDate.getDate() + 6)

      const startDayOfWeek = formatDateString(startDate, 'EEEE').toLowerCase()
      const endDayOfWeek = formatDateString(endDate, 'EEEE').toLowerCase()

      this.dateTimeRange = {
        startDate: startDate,
        endDate: endDate,
      }

      this.tempDate.dayRange = this.dayRange

      this.dayRange.splice(0, 1, startDayOfWeek)
      this.dayRange.splice(1, 1, endDayOfWeek)
    },
    setFilter(filterView) {
      this.attendance = []
      this.filterView = filterView

      if (filterView === 'Week') {
        this.initializeFilterForWeek(this.currentDate)
      }

      this.applyDateFilter()
      this.forceRenderCalendar++
    },
    applyDateFilter() {
      this.$emit('applyDate', this.currentDate)
    },
    setTitleWrapperHeight() {
      const el = this.$refs.titleWrapper
      if (el) {
        const height = el.offsetHeight // or el.getBoundingClientRect().height
        this.$store.commit('layout/SET_TITLE_WRAPPER_HEIGHT', height)
      }
    },
  },
}
</script>

<style scoped>
.sm-button {
  min-width: 125px;
  font-weight: 500;
  line-height: 28px;
}
@media screen and (max-width: 1280px) {
  .sm-button {
    min-width: 110px;
    min-height: 40px;
  }
}
@media screen and (max-width: 640px) {
  .sm-button {
    min-width: 90px;
    min-height: 36px;
  }
}
</style>
